import {Box, Button, Divider, Group} from "@mantine/core";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {DateInput} from "../../date/DateInput/DateInput";
import {isBefore} from "@ots-frontend/ots-data"
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntityMultiSelect} from "../../select/entity/EntityMultiSelect/EntityMultiSelect";
import {IconCaretDown, IconCaretUp} from "@tabler/icons-react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

type ContainerDNUStatisticsSearchToolbarProps = {
  defaultValues?: {
    fromDate?: Date
    toDate?: Date,
    entityType?: string,
    entityIds?: string[]
  }
  onSubmit?: (value: {
    fromDate: Date
    toDate: Date | undefined
    entityType: string | undefined
    entityIds: string[] | undefined
  }) => void
  loading?: boolean
}

export const ContainerDNUStatisticsSearchToolbar = (props: ContainerDNUStatisticsSearchToolbarProps) => {
  const {defaultValues, onSubmit, loading} = props;

  const [showAdvanced, setShowAdvanced] = useState<boolean>(false)

  const {regionId} = useApp()
  const {t} = useTranslation(["common"]);

  useEffect(() => {
    if (defaultValues?.entityType || (defaultValues?.entityIds && defaultValues.entityIds.length > 0)) {
      setShowAdvanced(true)
    }
  }, [])

  const form = useForm({
    initialValues: {
      fromDate: defaultValues?.fromDate,
      toDate: defaultValues?.toDate,
      entityType: defaultValues?.entityType,
      entityIds: defaultValues?.entityIds
    },
    validate: {
      toDate: (value, values) => {
        let error: string | undefined = undefined
        if (value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }
        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fromDate: values.fromDate!,
        toDate: values.toDate,
        entityType: values.entityType,
        entityIds: values.entityIds
      })
    }
  }, [form, onSubmit])

  return (
    <Group position="apart" p="xs">
      <Group position="center">

      </Group>
      <Group position="center">
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <Group position="center">
            <DateInput
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.toolbar.fromtodate.form.field.fromdate", "From Date")}
              placeholder={t<string>("common.ui.toolbar.fromtodate.form.field.fromdate", "From Date")}
              clearable
              withAsterisk
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              required
              {...form.getInputProps("fromDate")} />
            <DateInput
              miw={100}
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.toolbar.fromtodate.form.field.todate", "To Date")}
              placeholder={t<string>("common.ui.toolbar.fromtodate.form.field.todate", "To Date")}
              clearable
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              {...form.getInputProps("toDate")} />
            <Button type="submit" loading={loading} variant="outline">
              {t("common.action.submit", {ns: "common"})}
            </Button>
            <Divider orientation="vertical"/>
            <Button
              rightIcon={showAdvanced ? <IconCaretUp/> : <IconCaretDown/>}
              variant="subtle"
              compact={true}
              onClick={() => setShowAdvanced(!showAdvanced)}>
              Advanced
            </Button>
          </Group>
          <Box hidden={!showAdvanced}>
            <EntityTypeSelect
              options={['ROUTEGROUP', 'ROUTE', 'SITE']}
              label={t("common.ui.toolbar.containerdnusearch.form.field.object", "Object")}
              placeholder={t<string>("common.toolbar.containerdnusearch.form.field.object.placeholder", "Object")}
              withinPortal={true}
              clearable
              {...form.getInputProps("entityType")} />
            <EntityMultiSelect
              regionId={regionId}
              entityType={form.getTransformedValues()?.entityType}
              pt="md"
              dropdownPosition="bottom"
              limit={20}
              withinPortal={true}
              clearable
              {...form.getInputProps("entityIds")} />
          </Box>
        </form>
      </Group>
      <Group position="center">

      </Group>
    </Group>
  );
};
